import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    CodeBlock, BlockType, SettingType, Define, ValidationMessage, SettingState, TibboDataType,
} from '@appblocks/codegeneration/types';
import * as micropython from './micropython';
import * as tios from './tios';
import * as zephyr from './zephyr';
import * as simulator from './simulator';

export default class HTTP extends BaseFeature {
    constructor() {
        super();
        this.name = 'HTTP Client';
        this.id = 'http';
        this.fileLinks = [

        ];
        this.files = [
            'libraries/http/http.tbh',
            'libraries/http/http.tbs',
        ];
    }

    getFiles(): Array<string> {
        if (this.project.runtime === 'tios') {
            return this.files;
        }
        return [];
    }

    getDefines(): Array<Define> {
        const defines = [];
        defines.push({
            name: 'HTTP_DEBUG_PRINT',
            value: '0',
        });

        return defines;
    }

    getDependencies() {
        const dependencies = [
            'sock',
            'dns',
        ];

        return dependencies;
    }

    getState(settingId: string): SettingState {
        if (settingId !== 'ENABLED' && this.getSettingValue('ENABLED').toString() === '0') {
            return SettingState.HIDDEN;
        }
        switch (settingId) {
            case 'ENABLED':
                return SettingState.ENABLED;
            default:
                return SettingState.ENABLED;
        }
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'micropython':
                return micropython.getCodeBlocks(this);
            case 'zephyr':
                return zephyr.getCodeBlocks(this);
            case 'simulator':
                return simulator.getCodeBlocks(this);
            case 'tios':
                return tios.getCodeBlocks(this);
            default:
                return [];
        }
    }

    getDetails(): string {
        return `
# HTTP Library

## Introduction<br />

The HTTP library simplifies the process of submitting Hypertext Transfer Protocol (HTTP) requests and dealing with the response on Tibbo's ARM-based programmable devices. This is an asynchronous and event-driven library that in most cases can be used to submit numerous concurrent requests up to a user-defined limit (the default is 10) — if that number is exceeded, additional requests will be ignored until a socket has been freed up to continue making HTTP requests. Connectivity is provided through the Ethernet (net.) and Wi-Fi (wln.) network interfaces.

Of the nine standard request methods for HTTP, this library facilitates the handling of the six most commonly used: 

1. GET requests a representation of the specified resource.
2. HEAD asks for the same as GET, only without the response body.
3. POST is used to submit an entity to the specified resource.
4. PUT replaces all current representations of the specified resource with the input.
5. DELETE eliminates the specified resource.
6. PATCH applies partial modifications to a resource.

When triggered by an event (e.g. a button press on the device or a scheduled activity), this library makes a Domain Name System (DNS) request for the specified target, establishes a connection to the provided Internet Protocol (IP) address and makes the desired HTTP requests. Once an HTTP request has been submitted, the library waits for a response, which is handled based on the user's predetermined needs — such as outputting returned data to a display — before the device is returned to an available state.

If the DNS request is unsuccessful, the process is terminated and the device returns to an available state.

## Library info<br />

Supported platforms: EM510, EM2000, EM2001, and second-generation Tibbo Project PCB (TPP) sizes 2 and 3. 

Dependencies: DHCP, DNS and SOCK libraries. The WLN library is highly recommended if using Wi-Fi.`;
    }
}
